import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

const STORAGE_KEY = 'rxk_site_images_cache_v1';

function readPersistedMap(): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePersistedMap(map: Record<string, string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage full or unavailable (private browsing) — in-memory cache
    // still works fine for the rest of this page load.
  }
}

// Seeded synchronously from localStorage — the map as of the last
// successful fetch, from any previous visit — instead of starting `null`
// on every fresh page load. That's what actually stops the hardcoded
// fallback image from ever painting on a reload after an admin swaps a
// picture: with a `null` start, the very first render always used to show
// each caller's fallbackUrl (the OLD image) while the real fetch was still
// in flight, then pop to the new one a moment later. A slightly-stale
// local snapshot beats a guaranteed-wrong fallback in the meantime, and
// the fetch below still runs immediately after to pick up anything that
// changed since — this variable is read once at module load purely to
// seed useState() below; loadImageMap()'s own fetch-vs-reuse decision is
// driven by `inFlight`, not by whether this is populated.
let cache: Record<string, string> | null = readPersistedMap();
let inFlight: Promise<Record<string, string>> | null = null;

async function fetchImageMap(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.from('rxk_site_images').select('key, url');
    const map: Record<string, string> = {};
    for (const row of (data as { key: string; url: string }[]) ?? []) map[row.key] = row.url;
    cache = map;
    writePersistedMap(map);
    return map;
  } catch {
    // Network/DB issue this load — fall back to whatever's already known
    // (the persisted snapshot, if any) rather than wiping it to empty.
    return cache ?? {};
  }
}

// Exported so main.tsx can kick this off before first paint, the same way
// src/i18n/index.ts's text-override fetch already gets a head start —
// gives a first-ever visitor (no localStorage snapshot yet) the shortest
// possible flash window instead of only benefiting repeat visitors.
export function loadImageMap(): Promise<Record<string, string>> {
  if (!inFlight) inFlight = fetchImageMap();
  return inFlight;
}

export function useSiteImage(key: string, fallbackUrl: string): string {
  const [url, setUrl] = useState(() => cache?.[key] ?? fallbackUrl);

  useEffect(() => {
    let mounted = true;
    loadImageMap().then((map) => {
      if (mounted && map[key] && map[key] !== url) setUrl(map[key]);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return url;
}
